import { useState, useEffect } from 'react';
import { Star, Shield, Award, TrendingUp, Clock, MessageCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { Progress } from './ui/progress';

// Trust tier badges
const TRUST_BADGES = {
  new: { icon: '🆕', color: 'text-gray-500', bg: 'bg-gray-100' },
  starter: { icon: '', color: 'text-[var(--theme-text-secondary)]', bg: 'bg-[var(--theme-hover)]' },
  established: { icon: '✓', color: 'text-blue-600', bg: 'bg-blue-50' },
  trusted: { icon: '⭐', color: 'text-[#F59E0B]', bg: 'bg-amber-50' },
  elite: { icon: '🏆', color: 'text-[#059669]', bg: 'bg-emerald-50' },
};

// Compact trust badge for cards/listings
export const TrustBadge = ({ tier, score, size = 'sm' }) => {
  const badge = TRUST_BADGES[tier] || TRUST_BADGES.new;
  const tierName = tier?.charAt(0).toUpperCase() + tier?.slice(1) || 'New';
  
  if (size === 'sm') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}>
        {badge.icon && <span>{badge.icon}</span>}
        {tierName}
      </span>
    );
  }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${badge.bg}`}>
      {badge.icon && <span className="text-lg">{badge.icon}</span>}
      <div>
        <span className={`font-semibold ${badge.color}`}>{tierName}</span>
        <span className="text-xs text-[var(--theme-text-muted)] ml-1">({score} pts)</span>
      </div>
    </div>
  );
};

// Star rating display
export const StarDisplay = ({ rating, count, size = 'sm' }) => {
  const stars = Math.round(rating);
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${iconSize} ${
              star <= stars ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-[var(--theme-text-primary)]">{rating.toFixed(1)}</span>
      {count !== undefined && (
        <span className="text-xs text-[var(--theme-text-muted)]">({count} reviews)</span>
      )}
    </div>
  );
};

// Full trust score breakdown card
export const TrustScoreCard = ({ breakdown, summary }) => {
  if (!breakdown) return null;
  
  const positiveTotal = 
    breakdown.completed_transactions_points +
    breakdown.review_points +
    breakdown.would_transact_again_points +
    breakdown.proof_of_funds_points +
    breakdown.account_age_points +
    breakdown.profile_complete_points +
    breakdown.response_rate_points +
    breakdown.login_frequency_points;
  
  const negativeTotal = 
    breakdown.special_interest_penalty +
    breakdown.dormancy_penalty +
    breakdown.dispute_penalty;
  
  const badge = TRUST_BADGES[breakdown.trust_tier] || TRUST_BADGES.new;
  
  return (
    <div className="rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-card)] overflow-hidden">
      {/* Header */}
      <div className={`p-6 ${badge.bg}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[var(--theme-text-muted)]">Trust Score</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-4xl font-black text-[var(--theme-text-primary)]">
                {breakdown.trust_score}
              </span>
              <span className="text-sm text-[var(--theme-text-muted)]">points</span>
            </div>
          </div>
          <div className="text-center">
            <span className="text-4xl">{badge.icon || '—'}</span>
            <p className={`font-semibold ${badge.color} mt-1`}>
              {breakdown.trust_tier?.charAt(0).toUpperCase() + breakdown.trust_tier?.slice(1)}
            </p>
          </div>
        </div>
        
        {/* Progress to next tier */}
        <div className="mt-4">
          <Progress value={Math.min(100, breakdown.trust_score)} className="h-2" />
          <div className="flex justify-between mt-1 text-xs text-[var(--theme-text-muted)]">
            <span>0</span>
            <span>New</span>
            <span>Starter</span>
            <span>Established</span>
            <span>Trusted</span>
            <span>Elite</span>
          </div>
        </div>
      </div>
      
      {/* Breakdown */}
      <div className="p-6 space-y-4">
        <h4 className="font-semibold text-[var(--theme-text-primary)] flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#059669]" />
          Score Breakdown
        </h4>
        
        {/* Positive factors */}
        <div className="space-y-2">
          <ScoreRow 
            icon={<CheckCircle className="w-4 h-4 text-[#059669]" />}
            label="Completed Transactions"
            value={`+${breakdown.completed_transactions_points}`}
            detail={`${breakdown.completed_transactions} transactions`}
            positive
          />
          <ScoreRow 
            icon={<Star className="w-4 h-4 text-[#F59E0B]" />}
            label="Review Ratings"
            value={`+${breakdown.review_points}`}
            detail={summary ? `${summary.total_reviews} reviews` : ''}
            positive
          />
          <ScoreRow 
            icon={<Award className="w-4 h-4 text-[#059669]" />}
            label="Would Transact Again"
            value={`+${breakdown.would_transact_again_points}`}
            positive
          />
          <ScoreRow 
            icon={<Shield className="w-4 h-4 text-blue-500" />}
            label="Proof of Funds"
            value={`+${breakdown.proof_of_funds_points}`}
            positive
          />
          <ScoreRow 
            icon={<Clock className="w-4 h-4 text-purple-500" />}
            label="Account Age"
            value={`+${breakdown.account_age_points}`}
            detail={`${breakdown.account_age_months} months`}
            positive
          />
          <ScoreRow 
            icon={<MessageCircle className="w-4 h-4 text-cyan-500" />}
            label="Response Rate"
            value={`+${breakdown.response_rate_points}`}
            detail={`${breakdown.response_rate_pct.toFixed(0)}%`}
            positive
          />
          <ScoreRow 
            label="Login Frequency"
            value={`+${breakdown.login_frequency_points}`}
            detail={breakdown.days_since_login === 0 ? 'Today' : `${breakdown.days_since_login}d ago`}
            positive
          />
        </div>
        
        {/* Negative factors */}
        {negativeTotal < 0 && (
          <>
            <div className="border-t border-[var(--theme-border)] pt-4">
              <h4 className="font-semibold text-[var(--theme-text-primary)] flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
                Penalties
              </h4>
            </div>
            <div className="space-y-2">
              {breakdown.special_interest_penalty < 0 && (
                <ScoreRow 
                  label="Special Interest No-Response"
                  value={breakdown.special_interest_penalty}
                />
              )}
              {breakdown.dormancy_penalty < 0 && (
                <ScoreRow 
                  label="Account Dormancy"
                  value={breakdown.dormancy_penalty}
                />
              )}
              {breakdown.dispute_penalty < 0 && (
                <ScoreRow 
                  label="Disputes"
                  value={breakdown.dispute_penalty}
                />
              )}
            </div>
          </>
        )}
        
        {/* Total */}
        <div className="border-t border-[var(--theme-border)] pt-4 flex justify-between items-center">
          <span className="font-semibold text-[var(--theme-text-primary)]">Total</span>
          <span className="text-xl font-bold text-[var(--theme-text-primary)]">
            {breakdown.trust_score} pts
          </span>
        </div>
      </div>
    </div>
  );
};

// Score row component
const ScoreRow = ({ icon, label, value, detail, positive }) => (
  <div className="flex items-center justify-between text-sm">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-[var(--theme-text-secondary)]">{label}</span>
      {detail && (
        <span className="text-xs text-[var(--theme-text-muted)]">({detail})</span>
      )}
    </div>
    <span className={`font-medium ${positive ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
      {value}
    </span>
  </div>
);

// Review summary for profile
export const ReviewSummary = ({ summary }) => {
  if (!summary || summary.total_reviews === 0) {
    return (
      <div className="text-center py-6 text-[var(--theme-text-muted)]">
        <Star className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p>No reviews yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Overall */}
      <div className="text-center pb-4 border-b border-[var(--theme-border)]">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Star className="w-6 h-6 fill-[#F59E0B] text-[#F59E0B]" />
          <span className="text-3xl font-black text-[var(--theme-text-primary)]">
            {summary.overall_avg_rating.toFixed(1)}
          </span>
        </div>
        <p className="text-sm text-[var(--theme-text-muted)]">
          {summary.total_reviews} total review{summary.total_reviews !== 1 ? 's' : ''}
        </p>
      </div>
      
      {/* As Seller */}
      {summary.seller_review_count > 0 && (
        <div className="space-y-2">
          <h5 className="font-semibold text-[var(--theme-text-primary)] text-sm">
            As a Seller ({summary.seller_review_count})
          </h5>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Overall</span>
              <StarDisplay rating={summary.seller_avg_overall} size="sm" />
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Communication</span>
              <span className="text-[var(--theme-text-primary)]">{summary.seller_avg_communication}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Item Accuracy</span>
              <span className="text-[var(--theme-text-primary)]">{summary.seller_avg_item_accuracy}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Item Quality</span>
              <span className="text-[var(--theme-text-primary)]">{summary.seller_avg_item_quality}/5</span>
            </div>
          </div>
          <p className="text-xs text-[#059669]">
            {summary.seller_would_transact_again_pct}% would buy again
          </p>
        </div>
      )}
      
      {/* As Buyer */}
      {summary.buyer_review_count > 0 && (
        <div className="space-y-2 pt-4 border-t border-[var(--theme-border)]">
          <h5 className="font-semibold text-[var(--theme-text-primary)] text-sm">
            As a Buyer ({summary.buyer_review_count})
          </h5>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Overall</span>
              <StarDisplay rating={summary.buyer_avg_overall} size="sm" />
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Communication</span>
              <span className="text-[var(--theme-text-primary)]">{summary.buyer_avg_communication}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Punctuality</span>
              <span className="text-[var(--theme-text-primary)]">{summary.buyer_avg_punctuality}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Payment</span>
              <span className="text-[var(--theme-text-primary)]">{summary.buyer_avg_payment_promptness}/5</span>
            </div>
          </div>
          <p className="text-xs text-[#059669]">
            {summary.buyer_would_transact_again_pct}% would sell again
          </p>
        </div>
      )}
    </div>
  );
};
